/**
 * WeekEditorModal.jsx - Sprint 2
 * 
 * Modal completo para editar una semana específica.
 * Contiene 6 tabs (B1-B6) para configurar todos los bloques.
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md
 */

import React, { useState, useEffect } from 'react';
import { 
  X,
  Save,
  Loader,
  AlertCircle,
  CheckCircle2,
  FileText,
  List,
  ClipboardCheck,
  Brain,
  FileCheck,
  Users,
  Plus,
  Trash2,
  Target
} from 'lucide-react';
import B1ResourcesEditor from './B1ResourcesEditor';
import B2ExecutionTemplateEditor from './B2ExecutionTemplateEditor';
import B3AuditCriteriaEditor from './B3AuditCriteriaEditor';
import B4CoachConfigEditor from './B4CoachConfigEditor';
import B5FormalizationTemplateEditor from './B5FormalizationTemplateEditor';
import B6CrossFeedbackCriteriaEditor from './B6CrossFeedbackCriteriaEditor';

const WeekEditorModal = ({ 
  isOpen, 
  onClose, 
  weekNumber, 
  weekData: initialWeekData,
  courseId,
  onSave 
}) => {
  const [activeTab, setActiveTab] = useState('b1');
  const [weekData, setWeekData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Inicializar datos de la semana
  useEffect(() => {
    if (isOpen && weekNumber) {
      setWeekData(initialWeekData || {
        weekNumber,
        title: `Semana ${weekNumber}`,
        learningObjectives: [],
        b1_resources: [],
        b2_execution_template: { instructions: '', fields: [] },
        b3_criteria: [],
        b4_coach_config: { enabled: true, evaluationFrequency: 'on-submit', focusAreas: [], customPromptAdditions: '' },
        b5_formalization_template: { format: 'structured', markdownContent: '', jsonSchema: '', sections: [] },
        b6_criteria: []
      });
      setHasChanges(false);
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [isOpen, weekNumber, initialWeekData]);

  const tabs = [
    { id: 'b1', label: 'B1: Recursos', icon: FileText, color: 'blue' },
    { id: 'b2', label: 'B2: Template', icon: List, color: 'green', required: true },
    { id: 'b3', label: 'B3: Auditoría', icon: ClipboardCheck, color: 'yellow' },
    { id: 'b4', label: 'B4: Coach IA', icon: Brain, color: 'purple' },
    { id: 'b5', label: 'B5: Formalización', icon: FileCheck, color: 'indigo' },
    { id: 'b6', label: 'B6: Feedback', icon: Users, color: 'pink' }
  ];

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await onSave(weekNumber, weekData);
      setSaveSuccess(true);
      setHasChanges(false);
      
      // Cerrar después de 1 segundo
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      setSaveError(error.message || 'Error guardando semana');
    } finally {
      setSaving(false);
    }
  };

  const updateWeekData = (field, value) => {
    setWeekData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Semana {weekNumber}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Configura los 6 bloques educativos de esta semana
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Información Básica de la Semana */}
        <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200">
          <div className="space-y-4">
            {/* Título de la semana */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título de la semana *
              </label>
              <input
                type="text"
                value={weekData?.title || ''}
                onChange={(e) => updateWeekData('title', e.target.value)}
                placeholder={`Semana ${weekNumber} - Escribe un título descriptivo`}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Objetivos de aprendizaje */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Objetivos de aprendizaje
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const newObjectives = [...(weekData?.learningObjectives || []), ''];
                    updateWeekData('learningObjectives', newObjectives);
                  }}
                  className="text-purple-600 hover:text-purple-700 text-sm flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Agregar objetivo
                </button>
              </div>
              <div className="space-y-2">
                {(weekData?.learningObjectives || []).map((objective, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    <input
                      type="text"
                      value={objective}
                      onChange={(e) => {
                        const newObjectives = [...weekData.learningObjectives];
                        newObjectives[idx] = e.target.value;
                        updateWeekData('learningObjectives', newObjectives);
                      }}
                      placeholder="Ej: Comprender el ciclo BUILD-MEASURE-LEARN"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newObjectives = weekData.learningObjectives.filter((_, i) => i !== idx);
                        updateWeekData('learningObjectives', newObjectives);
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {(!weekData?.learningObjectives || weekData.learningObjectives.length === 0) && (
                  <p className="text-sm text-gray-500 italic">No hay objetivos definidos. Haz clic en "Agregar objetivo" para comenzar.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 bg-gray-50">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? `border-${tab.color}-600 text-${tab.color}-600`
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.required && <span className="text-red-500">*</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Messages */}
          {saveSuccess && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <p className="text-green-700 text-sm">Semana guardada exitosamente. Cerrando...</p>
            </div>
          )}
          {saveError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700 text-sm">{saveError}</p>
            </div>
          )}

          {/* Tab Content - B1 Resources */}
          {activeTab === 'b1' && weekData && (
            <B1ResourcesEditor
              resources={weekData.b1_resources || []}
              onChange={(resources) => updateWeekData('b1_resources', resources)}
            />
          )}

          {/* Tab Content - B2 Execution Template */}
          {activeTab === 'b2' && weekData && (
            <B2ExecutionTemplateEditor
              template={weekData.b2_execution_template || { instructions: '', fields: [] }}
              onChange={(template) => updateWeekData('b2_execution_template', template)}
            />
          )}

          {/* Tab Content - B3 Audit Criteria */}
          {activeTab === 'b3' && weekData && (
            <B3AuditCriteriaEditor
              criteria={weekData.b3_criteria || []}
              onChange={(criteria) => updateWeekData('b3_criteria', criteria)}
            />
          )}

          {/* Tab Content - B4 Coach Config */}
          {activeTab === 'b4' && weekData && (
            <B4CoachConfigEditor
              config={weekData.b4_coach_config || { enabled: true, evaluationFrequency: 'on-submit', focusAreas: [], customPromptAdditions: '' }}
              onChange={(config) => updateWeekData('b4_coach_config', config)}
            />
          )}

          {/* Tab Content - B5 Formalization Template */}
          {activeTab === 'b5' && weekData && (
            <B5FormalizationTemplateEditor
              template={weekData.b5_formalization_template || { format: 'structured', markdownContent: '', jsonSchema: '', sections: [] }}
              onChange={(template) => updateWeekData('b5_formalization_template', template)}
            />
          )}

          {/* Tab Content - B6 Cross-Feedback Criteria */}
          {activeTab === 'b6' && weekData && (
            <B6CrossFeedbackCriteriaEditor
              criteria={weekData.b6_criteria || []}
              onChange={(criteria) => updateWeekData('b6_criteria', criteria)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between rounded-b-lg">
          <div className="text-sm text-gray-600">
            {hasChanges && (
              <span className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="w-4 h-4" />
                Cambios sin guardar
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Guardar Semana
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekEditorModal;
